/**
 * Per-card row on the Recommend page.
 *
 * Renders:
 *   - Card identity (bank + name + last4 if present)
 *   - Headline reward (ALWAYS the cap-adjusted value the engine returns)
 *   - Rule that fired + effective rate
 *   - Cap annotation when cap is binding for this transaction
 *   - Reward portion breakdown when there's a spillover or milestone bonus
 *   - "Refine" toggle that mounts <RefinementPanel> when expanded
 *
 * Design rule: NEVER display a pre-cap "what could have been" number. The
 * engine clamps to caps; we just label the clamp.
 */
import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, AlertTriangle, Trophy, ArrowRight, Sparkles, Calculator } from 'lucide-react';
import { formatINR, formatPct } from '@/lib/format';
import { cn } from '@/lib/utils';
import { RefinementPanel } from './RefinementPanel';

const portionGlyph = (kind) => {
  if (kind === 'primary') return null;
  if (kind === 'spillover') return <ArrowRight className="h-3 w-3" />;
  if (kind === 'milestone_bonus') return <Trophy className="h-3 w-3" />;
  if (kind === 'tier_progress') return <Sparkles className="h-3 w-3" />;
  return null;
};

const portionTone = (kind) => {
  if (kind === 'milestone_bonus') return 'text-emerald-600 dark:text-emerald-400';
  if (kind === 'spillover') return 'text-amber-600 dark:text-amber-400';
  if (kind === 'tier_progress') return 'text-purple-600 dark:text-purple-400';
  return 'text-foreground/80';
};

const portionLabel = (kind) => {
  if (kind === 'primary') return 'Primary';
  if (kind === 'spillover') return 'Spillover';
  if (kind === 'milestone_bonus') return 'Milestone bonus';
  if (kind === 'tier_progress') return 'Tier bonus';
  return kind;
};

export const CardRow = ({
  rank,
  ranked,                   // RankedCard from engine
  refinementSchema,         // { questions: [] } from computeCardRefinement
  answers,                  // current refinement answers for this card
  onChangeAnswers,          // (cardId, nextAnswers) => void
  cardId,
  isLoading,
}) => {
  const [expanded, setExpanded] = useState(false);

  const hasRefinement = (refinementSchema?.questions || []).length > 0;
  const portions = ranked?.portions || [];

  const headlineRule = useMemo(() => {
    if (ranked?.rule_label) return ranked.rule_label;
    const prim = portions.find((p) => p.kind === 'primary');
    return prim?.rule_label || '—';
  }, [ranked, portions]);

  const capRemaining = ranked?.cap_remaining_inr;
  const showCap = capRemaining != null && capRemaining < 1e9; // ignore "no cap" sentinel
  const capBinding = ranked?.cap_binding;
  const effRate = ranked?.effective_rate_pct;
  const rewardValue = ranked?.reward_value_inr || 0;
  const isWinner = rank === 1;

  return (
    <Card
      className={cn(
        'transition-colors',
        isWinner && 'border-accent/40 bg-accent/[0.02]',
        hasRefinement && expanded && 'border-accent/30'
      )}
      data-testid={`recommend-card-row-${cardId}`}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          {/* Identity */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className={cn(
                  'inline-flex items-center justify-center h-6 w-6 rounded-full text-[11px] font-semibold tabular-nums',
                  isWinner ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
                )}
                data-testid={`recommend-card-rank-${cardId}`}
              >
                {rank}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {ranked?.card_bank}
              </span>
              {isWinner && (
                <Badge variant="default" className="text-[9px] uppercase tracking-wider h-4 px-1.5">
                  Best
                </Badge>
              )}
            </div>
            <div className="font-display text-lg font-semibold text-foreground truncate" data-testid={`recommend-card-name-${cardId}`}>
              {ranked?.card_name}
            </div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
              <span className="truncate">{headlineRule}</span>
              {effRate != null && (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <span className="tabular-nums">{formatPct(effRate)} effective</span>
                </>
              )}
            </div>
          </div>

          {/* Headline reward */}
          <div className="text-right shrink-0">
            <div
              className={cn(
                'font-display text-2xl font-bold tabular-nums',
                rewardValue > 0 ? 'text-foreground' : 'text-muted-foreground'
              )}
              data-testid={`recommend-card-reward-${cardId}`}
            >
              {formatINR(rewardValue)}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              for this txn
            </div>
          </div>
        </div>

        {/* Cap annotation */}
        {showCap && (
          <div
            className={cn(
              'mt-3 text-xs flex items-center gap-1.5',
              capBinding ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
            )}
            data-testid={`recommend-card-cap-${cardId}`}
          >
            {capBinding && <AlertTriangle className="h-3 w-3" />}
            <span>
              {capBinding
                ? `Reward capped at ${formatINR(rewardValue)} (rule cap)`
                : `Cap headroom: ${formatINR(capRemaining)} remaining`}
            </span>
          </div>
        )}

        {/* Calculator view — earning math broken down by portion */}
        {portions.length > 0 && (
          <div
            className="mt-3 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs space-y-1.5"
            data-testid={`recommend-card-calculator-${cardId}`}
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              <Calculator className="h-3 w-3" /> Earning math
            </div>
            {portions.map((p, idx) => {
              const spend = p.eligible_amount_inr || 0;
              const reward = p.reward_value_inr || 0;
              const rate = spend > 0 ? (reward / spend) * 100 : 0;
              const ruleLbl = (p.rule_label || '').slice(0, 60);
              const note = (p.note || '').toLowerCase();
              const isCapped = note.includes('cap');
              return (
                <div
                  key={idx}
                  className={cn('flex items-center justify-between gap-3', portionTone(p.kind))}
                  data-testid={`recommend-card-portion-${cardId}-${p.kind}-${idx}`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    {portionGlyph(p.kind)}
                    <span className="tabular-nums text-foreground/90">
                      {formatINR(spend)}
                    </span>
                    {rate > 0 && (
                      <span className="text-muted-foreground">@ {rate.toFixed(2)}%</span>
                    )}
                    <span className="text-muted-foreground/70 truncate">
                      · {portionLabel(p.kind)}{ruleLbl ? `: ${ruleLbl}` : ''}
                    </span>
                  </div>
                  <span className="font-semibold tabular-nums shrink-0">
                    {reward >= 0 ? '+' : ''}{formatINR(reward)}
                  </span>
                </div>
              );
            })}
            <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-border/60">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</span>
              <span className="font-bold tabular-nums">{formatINR(rewardValue)}</span>
            </div>
          </div>
        )}

        {/* Refine toggle */}
        {hasRefinement && (
          <div className="mt-4 pt-3 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs h-7 px-2 -mx-2 text-muted-foreground hover:text-foreground"
              data-testid={`recommend-card-refine-toggle-${cardId}`}
            >
              {expanded ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
              {expanded ? 'Hide refinements' : `Refine — ${refinementSchema.questions.length} ${refinementSchema.questions.length === 1 ? 'question' : 'questions'}`}
            </Button>
            {expanded && (
              <div className="mt-3">
                <RefinementPanel
                  schema={refinementSchema}
                  answers={answers}
                  onChange={(nextAnswers) => onChangeAnswers(cardId, nextAnswers)}
                  isLoading={isLoading}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
